import { useFormik } from "formik";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "../../../../components/buttons/button";
import { TextInput } from "../../../../components/inputs/textInput";
import { InfoComponent } from "../../../../components/notification/infoComponent";
import { AuthHeader } from "../../../../components/typography/authHeader";
import { BaseApiErrorResponse } from "../../../../utils/types";
import { authSliceActions } from "../../../../redux/features/auth/auth.slice";
import { useAppDispatch } from "../../../../redux/hooks";
import { Icon } from "../../../../components/icon/icon";
import {
  useGetSingleAgentQuery,
  useSalesSignupMutation,
} from "../../../../redux/features/sales/auth/auth.api";
import { SalesSignupSchema } from "../../../../utils/validations";
import { useEffect } from "react";

export const SalesSignupPage = () => {
  const [signup, { isLoading, isError, error }] = useSalesSignupMutation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const rawName = searchParams.get("name") || "";
  const rawEmail = searchParams.get("email") || "";

  const inviteeName = rawName.replace(/\+/g, " ").trim();
  const inviteeEmail = rawEmail.replace(/ /g, "+").trim();

  const { isFetching: fetchingSingleAgent, isError: singleAgentError } =
    useGetSingleAgentQuery(
      {
        email: inviteeEmail,
      },
      {
        refetchOnMountOrArgChange: true,
        skip: !inviteeEmail,
      },
    );

  useEffect(() => {
    if (!fetchingSingleAgent && singleAgentError) {
      navigate("/expired/invite");
    }
  }, [singleAgentError, navigate, fetchingSingleAgent]);

  const { values, handleBlur, handleChange, handleSubmit, errors, touched } =
    useFormik({
      initialValues: {
        email: inviteeEmail || "",
        password: "",
      },
      validationSchema: SalesSignupSchema,
      onSubmit: async (vals) => {
        const response = await signup({
          email: vals.email,
          password: vals.password,
        });

        if (response.data?.status === "success") {
          dispatch(
            authSliceActions.signSalesIn({
              profile: {
                ...response.data.data,
              },
              token: response.data.data.token,
            }),
          );
          navigate("/dashboard");
        }
      },
    });

  if (fetchingSingleAgent) {
    return null;
  }

  return (
    <form
      className="w-full rounded-2xl bg-white px-5 py-16 md:p-8"
      onSubmit={handleSubmit}
    >
      <AuthHeader
        header={
          <div className="flex flex-col items-center justify-center gap-2.5">
            <div className="h-[100px] w-fit">
              <img
                src="/assets/salesImage.svg"
                alt="sales"
                className="w-full"
              />
            </div>
            <span>
              You’ve been invited <br /> to join Tikket Sales
            </span>
          </div>
        }
        text={
          <span>Please create a password to complete your account setup.</span>
        }
      />

      <div className="mt-10 flex flex-col gap-3">
        {isError && (
          <InfoComponent
            title="Error"
            text={
              (error as unknown as BaseApiErrorResponse)?.data?.message ??
              "An error occurred!"
            }
            type="error"
          />
        )}
        <div className="mb-4 flex gap-2 rounded-lg border border-grey-100 p-[14px]">
          <Icon
            svg="user-black"
            width={18}
            height={18}
            containerClassName="bg-grey-100 w-[28px] h-[28px] flex justify-center items-center rounded-[438px]"
          />
          <div className="flex flex-col">
            <p className="font-bold text-grey-950">{inviteeName}</p>
            <p className="text-sm font-semibold text-grey-700">
              {inviteeEmail}
            </p>
          </div>
        </div>

        <TextInput
          type="password"
          name="password"
          value={values.password}
          placeholder="Enter your password"
          onChange={handleChange}
          onBlur={handleBlur}
          hasTitle
          title="Password"
          errors={errors.password && touched.password ? errors.password : ""}
        />
        <div className="z-10 flex w-full flex-col gap-2.5">
          <Button
            type="submit"
            text="Create Password"
            variation="primary"
            className="mt-5"
            loading={isLoading}
            disabled={isLoading}
          />
          <span className="text-center text-xs text-grey-700">
            By clicking “Create Password”, you agree to our <br />{" "}
            <Link
              to={"/legal/terms-of-use"}
              className="underline underline-offset-2"
            >
              Term of use
            </Link>{" "}
            and{" "}
            <Link
              to={"/legal/privacy-policy"}
              className="underline underline-offset-2"
            >
              Privacy Policy
            </Link>
          </span>
        </div>
      </div>
    </form>
  );
};
